/**
 * Coups de pouce — Attendu 1112 « Placer sur une droite graduée un point dont
 * l'abscisse est un nombre relatif » (automatisme 4e).
 *
 * placer_* (placement interactif, QCM) : entrée PARTAGÉE — lire d'abord ce
 * que vaut une graduation, partir de l'origine (ou du repère le plus proche),
 * compter vers la gauche pour un négatif, vérifier avec l'ordre. L'exemple :
 * placer −3,5 sur une droite graduée de 0,5 en 0,5 (7 graduations à gauche
 * de 0), pièges 3,5 graduations et le côté droit. Valeurs hors du gen (le
 * gen en demis va de −3,5 à 3,5 avec repères 0 et 1 ; ici repères 0, 1 et 2).
 *
 * vf_tableau_vf : la méthode pour vérifier une affirmation de placement (sens,
 * nombre de graduations depuis le repère le plus proche, entre quelles
 * graduations, ordre). L'exemple : la droite de −2 à 0 graduée en dixièmes,
 * placer −1,3 : on part de −1 et on compte 3 graduations vers la gauche.
 *
 * rappel_tableau_vf : pur rappel, pas d'entrée (doctrine 2026-07-31).
 */

// ─── Droite graduée SVG : de `min` à `max`, pas `pas`, repères étiquetés ────
const droite = ({ min, max, pas, reperes, x0 = 20, x1 = 340, y = 150, tick = 7 }) => {
    const px = (v) => x0 + ((v - min) / (max - min)) * (x1 - x0);
    let s = `<line x1="${x0 - 8}" y1="${y}" x2="${x1 + 12}" y2="${y}" stroke="#334155" stroke-width="1.6"/><polygon points="${x1 + 18},${y} ${x1 + 9},${y - 4} ${x1 + 9},${y + 4}" fill="#334155"/>`;
    const n = Math.round((max - min) / pas);
    for (let k = 0; k <= n; k++) {
        const v = Math.round((min + k * pas) * 1000) / 1000;                     // indice entier : pas d'accumulation flottante (0,1 + 0,1 + …)
        const r = reperes.some((q) => Math.abs(q - v) < 1e-9); const h = r ? tick + 3 : tick;
        s += `<line x1="${px(v).toFixed(1)}" y1="${y - h}" x2="${px(v).toFixed(1)}" y2="${y + h}" stroke="#334155" stroke-width="${r ? 1.6 : 1}"/>`;
        if (r) s += `<text x="${px(v).toFixed(1)}" y="${y + 24}" font-family="system-ui,sans-serif" font-size="12" text-anchor="middle" fill="#1e293b">${String(v).replace('.', ',')}</text>`;
    }
    return { svg: s, px };
};
const POINT = (x, y, label, couleur, dy = -12) => `<circle cx="${x.toFixed(1)}" cy="${y}" r="4.5" fill="${couleur}"/><text x="${x.toFixed(1)}" y="${y + dy}" font-family="system-ui,sans-serif" font-size="13" font-weight="800" text-anchor="middle" fill="${couleur}">${label}</text>`;
const FLECHE = (xa, xb, y, couleur) => { const d = xb > xa ? 1 : -1; return `<line x1="${xa.toFixed(1)}" y1="${y}" x2="${(xb - d * 6).toFixed(1)}" y2="${y}" stroke="${couleur}" stroke-width="2.2" stroke-dasharray="5 3"/><polygon points="${xb.toFixed(1)},${y} ${(xb - d * 9).toFixed(1)},${y - 4.5} ${(xb - d * 9).toFixed(1)},${y + 4.5}" fill="${couleur}"/>`; };
const T = (x, y, s, couleur, poids = 700, taille = 11) => `<text x="${x}" y="${y}" font-size="${taille}" font-weight="${poids}" fill="${couleur}">${s}</text>`;
const STYLE = `<style>@keyframes cp1112-pop { from { opacity: 0; } to { opacity: 1; } } .cp1112-pop { opacity: 0; animation: cp1112-pop 0.5s ease forwards; }</style>`;
const CAPTIONS = (c1, c2, c3) => `<text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">${c1}</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">${c2}</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">${c3}</text>`;
const SVG = (aria, captions, e1, e2, e3, h = 300) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 ${h}" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3" aria-label="${aria}">
  ${STYLE}
  ${captions}
  <g data-etape="1" font-family="system-ui,sans-serif">${e1}</g>
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1112-pop">${e2}</g></g>
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1112-pop">${e3}</g></g>
</svg>`.trim();

// ─── Placer −3,5 sur une droite graduée de 0,5 en 0,5 ──────────────────────
const D1 = droite({ min: -4, max: 4, pas: 0.5, reperes: [0, 1, 2], y: 140 });
// Deux couleurs (demande prof 2026-09-11) : l'ÉCART entre les nombres écrits (1) et le nombre de PAS (2),
// sur la droite et dans le calcul 1 ÷ 2.
const C_ECART = '#DC2626'; const C_PAS = '#7C3AED';
/** Entre les nombres écrits a et b : les n pas (sauts violets au-dessus de la droite) et l'écart (accolade rouge plus haut). */
const ECART_ET_PAS = (D, a, b, n) => {
    const x0 = D.px(a); const x1 = D.px(b); const y = 140;
    const saut = (u, v) => `<path d="M ${u.toFixed(1)},${y - 12} Q ${((u + v) / 2).toFixed(1)},${y - 22} ${v.toFixed(1)},${y - 12}" fill="none" stroke="${C_PAS}" stroke-width="1.8"/>`;
    let s = '';
    for (let k = 0; k < n; k++) s += saut(x0 + ((x1 - x0) * k) / n, x0 + ((x1 - x0) * (k + 1)) / n);
    s += `<text x="${((x0 + x1) / 2).toFixed(1)}" y="${y - 25}" font-size="11" font-weight="800" text-anchor="middle" fill="${C_PAS}">${n} pas</text>`;
    s += `<path d="M ${x0.toFixed(1)},${y - 37} L ${x0.toFixed(1)},${y - 41} L ${x1.toFixed(1)},${y - 41} L ${x1.toFixed(1)},${y - 37}" fill="none" stroke="${C_ECART}" stroke-width="1.8"/>`;
    s += `<text x="${((x0 + x1) / 2).toFixed(1)}" y="${y - 45}" font-size="11" font-weight="800" text-anchor="middle" fill="${C_ECART}">écart : ${String(b - a).replace('.', ',')}</text>`;
    return s;
};
const SVG_PLACER = SVG(
    "Question : où placer le point A d'abscisse −3,5 sur cette droite graduée ? En 3 étapes à faire défiler avec les flèches. Étape 1 : on lit d'abord ce que vaut un pas : de 0 à 1 il y a 2 pas, donc un pas vaut 1 ÷ 2 = 0,5. Étape 2 : −3,5 est négatif, on part de l'origine vers la gauche, et 3,5 c'est 7 fois 0,5, donc on compte 7 pas vers la gauche. Étape 3 : on vérifie, A est entre −4 et −3, à gauche de −3. Réponse : A est 7 pas à gauche de l'origine",
    CAPTIONS('Combien vaut un pas ?', 'Vers la gauche, et on compte', 'On vérifie la position'),
    `${T(8, 44, 'Sur cette droite graduée, 0, 1 et 2 sont écrits.', '#1e293b')}
    ${T(8, 60, 'Question : où placer le point A d\'abscisse −3,5 ?', '#1E3A8A')}
    ${D1.svg}
    ${ECART_ET_PAS(D1, 0, 1, 2)}
    <text x="8" y="196" font-size="11" font-weight="700" fill="#1E3A8A">De 0 à 1, il y a <tspan fill="${C_PAS}">2 pas</tspan> :</text>
    <text x="8" y="210" font-size="11" font-weight="700" fill="#1E3A8A">un pas vaut <tspan fill="${C_ECART}">1</tspan> ÷ <tspan fill="${C_PAS}">2</tspan> = 0,5.</text>`,
    `${FLECHE(D1.px(0), D1.px(-3.5), 112, '#1D9E75')}${POINT(D1.px(-3.5), 140, 'A', '#1D9E75')}
    ${T(8, 234, '−3,5 est négatif : on part de l\'origine vers la gauche.', '#1D9E75')}${T(8, 248, '3,5, c\'est 7 fois 0,5 : on compte 7 pas. ✓', '#1D9E75')}`,
    `${T(8, 270, 'A est entre −4 et −3, juste à gauche de −3 : cohérent.', '#B45309')}
    ${T(8, 292, 'Réponse : A se place 7 pas à gauche de l\'origine.', '#1e293b', 700, 11.5)}`,
);

const AIDE_PLACER = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**D'abord le pas** : prends deux nombres écrits sur la droite et compte combien de pas les séparent. Le pas vaut leur écart divisé par ce nombre : de 0 à 1 en 2 pas, chaque pas vaut 1 ÷ 2 = 0,5 ; de 0 à 1 en 10 pas, chaque pas vaut 0,1 ; de 0 à 2 en 1 pas, chaque pas vaut 2.",
                "**Le signe donne le côté** : un nombre négatif se place à gauche de l'origine, un nombre positif à droite. −3 est à gauche de 0, à la même distance que 3.",
                "**On compte en pas, pas en unités** : pour placer −3,5 quand un pas vaut 0,5, on compte 7 pas vers la gauche. Si l'origine n'est pas écrite, on part du nombre écrit le plus proche.",
                "**Vérifie l'ordre** : plus on va vers la gauche, plus les abscisses sont petites. −3,5 est entre −4 et −3, plus à gauche que −3.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_PLACER },
    ],
};

// ─── V/F : la droite de −2 à 0 graduée en dixièmes, placer −1,3 ────────────
const D2 = droite({ min: -2, max: 0, pas: 0.1, reperes: [-2, -1, 0], y: 140 });
const SVG_VF = SVG(
    "Question : l'affirmation « pour placer −1,3, on part de −1 et on compte 3 pas vers la gauche » est-elle vraie ? En 3 étapes à faire défiler avec les flèches. Étape 1 : de −2 à −1, l'écart est 1 et il y a 10 pas, donc un pas vaut 1 ÷ 10 = 0,1. Étape 2 : −1,3 est plus petit que −1, donc à gauche de −1. Étape 3 : on vérifie, −1,3 est entre −2 et −1, plus près de −1. Réponse : l'affirmation est vraie",
    CAPTIONS('Combien vaut un pas ?', 'De quel côté, et combien ?', 'On vérifie la position'),
    `${T(8, 44, 'Sur cette droite graduée, −2, −1 et 0 sont écrits.', '#1e293b')}
    ${T(8, 60, 'Question : « pour placer −1,3, on part de −1 et on compte', '#1E3A8A')}${T(8, 74, '3 pas vers la gauche ». Vrai ou faux ?', '#1E3A8A')}
    ${D2.svg}
    ${ECART_ET_PAS(D2, -2, -1, 10)}
    <text x="8" y="196" font-size="11" font-weight="700" fill="#1E3A8A">De −2 à −1, il y a <tspan fill="${C_PAS}">10 pas</tspan> :</text>
    <text x="8" y="210" font-size="11" font-weight="700" fill="#1E3A8A">un pas vaut <tspan fill="${C_ECART}">1</tspan> ÷ <tspan fill="${C_PAS}">10</tspan> = 0,1.</text>`,
    `${FLECHE(D2.px(-1), D2.px(-1.3), 108, '#1D9E75')}${POINT(D2.px(-1.3), 140, '−1,3', '#1D9E75', 32)}
    ${T(8, 240, '−1,3 est plus petit que −1 : à gauche de −1. ✓', '#1D9E75')}`,
    `${T(8, 270, '−1,3 est bien entre −2 et −1, plus près de −1 : cohérent.', '#B45309')}
    ${T(8, 292, 'Réponse : l\'affirmation est vraie.', '#1e293b', 700, 11.5)}`,
);

const AIDE_VF = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                "**Commence par le pas** : compte les pas entre deux nombres écrits et divise leur écart par ce nombre pour savoir ce que vaut un pas. C'est ce qui décide du nombre de pas à compter.",
                "**Le côté** : un négatif se place à gauche de l'origine ; et entre deux négatifs, le plus petit est le plus à gauche (−4 est à gauche de −1).",
                "**Le nombre de pas** : pars du nombre écrit le plus proche, et divise l'écart par la valeur d'un pas. De −1 à −1,3 : 0,3, soit 3 pas de 0,1.",
                "**Entre quels nombres écrits** : −2,5 est entre −3 et −2, pas entre −2 et −1. Le premier chiffre après la virgule ne change pas l'entier de gauche.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_VF },
    ],
};

const CP_1112 = {
    placer_placer_points: AIDE_PLACER,
    placer_qcm_unique: AIDE_PLACER,
    vf_tableau_vf: AIDE_VF,
};

export default CP_1112;
